import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { withLocalErrorHandling } from "../../../../../api/handleErrorLocally";
import { formatEuro } from "../../../../../format/formatNumber";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { useSystemDropdownOptions } from "../../../../../hooks/useSystemDropdownOptions";
import {
  ApplyClaimBalanceSurcharge,
  SystemDropdownCategory,
  SystemDropdownParameter,
} from "../../../../api/ClientFileApi";
import { useClaimsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { useOptionalClaimSelection } from "../../../context/ClaimSelectionContext";
import { useClientClaims } from "../../../hooks/useClientClaims";
import { processableClaimIdsOf } from "../../../utils/claimSelection";
import { BalanceSurchargeFormValues } from "../BalanceSurchargeDialog.types";
import {
  balanceSurchargeValidationSchema,
  projectedTotalOf,
  surchargeRowsOf,
  surchargeScopeOf,
} from "../BalanceSurchargeDialog.utils";

export function useBalanceSurcharge(clientId: string, onClose: () => void) {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const claimsApi = useClaimsApi();
  const queryClient = useQueryClient();
  const selection = useOptionalClaimSelection();
  const selectedClaimIds = selection?.selectedClaimIds ?? new Set<string>();

  // Created once rather than per render, so `enableReinitialize` does not see a new date every time
  // and reset the form under the user.
  const [today] = useState(() => new Date());

  // The same query the list reads, so opening the dialog serves from cache rather than refetching.
  const { data: claims = [], isLoading: areClaimsLoading } = useClientClaims(clientId);

  // The complete list, the SRV-registered ("angemeldet") type included: the dialog offers that one
  // disabled with the reason, rather than leaving a configured value out without a trace.
  const entryTypes = useSystemDropdownOptions(SystemDropdownCategory.ClaimBalanceEntryType);

  const isSrvRegisteredEntryType = (entryType: string | null | undefined) =>
    entryTypes.booleanParameterOf(entryType, SystemDropdownParameter.SrvRegistered);

  const applyMutation = useMutation({
    mutationFn: (request: ApplyClaimBalanceSurcharge) => {
      if (!claimsApi.applyClaimBalanceSurcharge) {
        throw new Error("ClientFileApi.claims.applyClaimBalanceSurcharge is not available");
      }
      return claimsApi.applyClaimBalanceSurcharge(
        { clientId, applyClaimBalanceSurchargeRequest: request },
        withLocalErrorHandling
      );
    },
    onSuccess: (response) => {
      // Every affected claim gained an entry, so each claim's own cache entry is stale too, not just
      // the list.
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientClaims, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClaimById], refetchType: "active" });
      // The server's totals, not the preview's: the preview projects from the cached list, the
      // response reports what was written — and what the list will show, past-dated entries included.
      showSuccess(
        t("claims.surcharge.successfullyApplied", {
          count: response.affectedClaimCount,
          previousTotal: formatEuro(response.previousTotal, i18n.language),
          newTotal: formatEuro(response.newTotal, i18n.language),
        })
      );
      selection?.clearSelection();
      onClose();
    },
    onError: () => {
      // Nothing was written, so the dialog stays open with the selection intact and the user can
      // try again. The list is refetched because a rejected request usually means it is out of date.
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientClaims, clientId] });
      showError(t("claims.surcharge.failed"));
    },
  });

  const formik = useFormikWithDirtyForm<BalanceSurchargeFormValues>({
    enableReinitialize: true,
    initialValues: {
      percentage: null,
      date: today,
      // Only a default: the first offered type marked as a projection, or nothing if none is. A
      // Supervisor may have cleared the flag or hidden the option, in which case the field starts
      // empty rather than prefilling a value the dropdown does not offer — hidden options stay in the
      // list so stored keys still resolve to a label, hence the explicit flag check. The options
      // arrive ordered by label from the backend, so "first" is the same on every load. Resolved once
      // the value list has loaded, which is also when the dialog first renders its fields.
      entryType:
        entryTypes.optionItems.find(
          (option) => !option.isHidden && entryTypes.booleanParameterOf(option.id, SystemDropdownParameter.Projected)
        )?.id ?? null,
    },
    validationSchema: balanceSurchargeValidationSchema(t),
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    // Awaited rather than fired off: Formik only clears `isSubmitting` for a submit handler that
    // returns a promise, and the submit button is disabled while it is set — a failed apply would
    // otherwise leave the dialog open with a dead button and no way to retry.
    onSubmit: async (values) => {
      if (values.date === null || values.percentage === null) {
        return;
      }

      const claimIds = processableClaimIdsOf(surchargeRowsOf(claims, selectedClaimIds, values.date, values.percentage));
      if (claimIds.length === 0) {
        return;
      }

      try {
        await applyMutation.mutateAsync({
          claimIds,
          percentage: values.percentage,
          date: values.date,
          entryType: values.entryType,
        });
      } catch {
        // Reported by the mutation's error handler; the dialog stays open so the values are kept.
      }
    },
  });

  const percentage = formik.values.percentage ?? 0;
  const rows = surchargeRowsOf(claims, selectedClaimIds, formik.values.date, percentage);
  const processableCount = processableClaimIdsOf(rows).length;
  const scope = surchargeScopeOf(rows);
  const newTotal = projectedTotalOf(rows);

  return {
    formik,
    entryTypeOptions: entryTypes.optionItems,
    isSrvRegisteredEntryType,
    rows,
    processableCount,
    skippedCount: rows.length - processableCount,
    scope,
    newTotal,
    difference: newTotal - scope.currentTotal,
    /** The preview stays blank until a usable percentage is entered, rather than showing a zero delta. */
    hasPreview: percentage > 0,
    isLoading: areClaimsLoading || entryTypes.isLoading,
  };
}
